import React, { useContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { NoRowsLayout } from '../../../../components/NoRows/NoRowsLayout';
import Loading from '../../../../components/Loader/Loading';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Card, CardContent, Chip, Grid, IconButton, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { Delete, DragIndicator, Edit, RemoveRedEye } from '@mui/icons-material';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { formatDateTime } from '../../../../utils/dateUtils';
import { SectionContext } from '../Section/Sections';
import { SectionServices } from '../Section/SectionServices';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../../../../hooks/useResponsive';
import Swal from 'sweetalert2';

const sectionTypeColors = {
    horizontal_scroll: { bg: '#e3f2fd', color: '#1565c0' },
    carousel: { bg: '#f3e5f5', color: '#7b1fa2' },
    grid_2_column: { bg: '#e8f5e9', color: '#2e7d32' },
    grid_3_column: { bg: '#fff3e0', color: '#e65100' },
    grid_4_column: { bg: '#fce4ec', color: '#c2185b' },
    fixed_banner: { bg: '#e0f7fa', color: '#00838f' },
    vertical_list: { bg: '#f1f8e9', color: '#558b2f' },
    mixed_grid: { bg: '#ede7f6', color: '#512da8' }
};

const backgroundTypeColors = {
    color: { bg: '#e3f2fd', color: '#1565c0' },
    image: { bg: '#f3e5f5', color: '#7b1fa2' },
    gradient: { bg: '#fff3e0', color: '#e65100' },
    none: { bg: '#f5f5f5', color: '#757575' }
};

const HeaderManagement = () => {
    const menuDispatch = useDispatch();
    useEffect(() => {
        menuDispatch({ type: 'menuSelect', payload: 'home-screen' });
    }, []);

    const { isSm } = useResponsive();
    const navigate = useNavigate();
    const {
        list,
        loading,
        setLoading,
        fetchSectionList,
        allRowCount,
        paginationModel,
        handlePaginationChange,
        handleSortingChange,
        sortModel,
        handleStatusChange,
        handleDeleteSection,
        handleFilterChange
    } = useContext(SectionContext);

    const [reorderList, setReorderList] = useState([]);
    const [originalOrder, setOriginalOrder] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        if (list) {
            const activeList = [...list]
                .filter(item => item.isActive)
                .sort((a, b) => a.position - b.position);
            setReorderList(activeList);
            setOriginalOrder(activeList);
        }
    }, [list]);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => setDragOverIndex(null);
    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newList = [...reorderList];
        const [draggedItem] = newList.splice(draggedIndex, 1);
        newList.splice(dropIndex, 0, draggedItem);
        setReorderList(newList);

        const payload = newList.map((item, idx) => ({
            id: item.id,
            position: idx + 1
        }));

        try {
            setLoading(true);
            const response = await SectionServices.reorderSections(payload);
            console.log('response',response)
            if (response.success == 1) {
                setOriginalOrder(newList);
                Swal.fire({ icon: 'success', title: 'Reordered successfully', text: response.message || 'Failed to update positions', timer: 2000 });

                if (typeof fetchSectionList == 'function') fetchSectionList();
            } else {
                setReorderList([...originalOrder]);
                Swal.fire({ icon: 'error', title: 'Reorder Failed', text: response.message || 'Failed to update positions', timer: 2000 });
            }
        } catch {
            setReorderList([...originalOrder]);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong', timer: 2000 });
        } finally {
            setLoading(false);
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const columns = [
        {
            field: 'position',
            headerName: 'Position',
            headerAlign: 'center',
            flex: 0.5,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 'bold'
                }}>
                    {params.row.position || '-'}
                </Box>
            )
        },
        {
            field: 'title',
            headerName: 'Title',
            headerAlign: 'center',
            flex: 1.5,
            sortable: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {params.row.title || 'N/A'}
                    </Typography>
                    {params.row.subtitle && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {params.row.subtitle}
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            field: 'sectionType',
            headerName: 'Section Type',
            headerAlign: 'center',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const type = params.row.sectionType;
                const colors = sectionTypeColors[type] || { bg: '#f5f5f5', color: '#757575' };
                return (
                    <Chip
                        label={type?.replace(/_/g, ' ') || 'N/A'}
                        size="small"
                        sx={{
                            backgroundColor: colors.bg,
                            color: colors.color,
                            fontSize: '0.65rem',
                            textTransform: 'capitalize',
                            fontWeight: 500
                        }}
                    />
                );
            }
        },
        {
            field: 'backgroundType',
            headerName: 'Background',
            headerAlign: 'center',
            flex: 0.8,
            sortable: false,
            renderCell: (params) => {
                const type = params.row.backgroundType;
                const colors = backgroundTypeColors[type] || { bg: '#f5f5f5', color: '#757575' };
                return (
                    <Chip
                        label={type || 'none'}
                        size="small"
                        sx={{
                            backgroundColor: colors.bg,
                            color: colors.color,
                            fontSize: '0.65rem',
                            textTransform: 'capitalize',
                            fontWeight: 500
                        }}
                    />
                );
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'center',
            flex: 0.6,
            sortable: false,
            renderCell: (params) => (
                <PermissionGuard feature='customer-app' permission='canEdit'>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', width: '100%' }}>
                        <Switch
                            size='small'
                            checked={params.row.isActive}
                            onChange={() => handleStatusChange({ isActive: !params.row.isActive }, params.row.id)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: 'success.main' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'success.main' },
                                '& .MuiSwitch-switchBase': { color: 'error.main' },
                                '& .MuiSwitch-track': { backgroundColor: 'error.main' }
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                        <Typography sx={{
                            color: params.row.isActive ? 'success.main' : 'error.main',
                            fontWeight: '600', fontSize: '0.6rem'
                        }}>
                            {params.row.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                    </Box>
                </PermissionGuard>
            )
        },
        {
            field: 'activePeriod',
            headerName: 'Active Period',
            headerAlign: 'center',
            flex: 1.2,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.6rem' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>Start: </Box>
                        {params.row.startDateTime ? formatDateTime(params.row.startDateTime, 'date') : 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>End: </Box>
                        {params.row.endDateTime ? formatDateTime(params.row.endDateTime, 'date') : 'N/A'}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'createdAt',
            headerName: 'Created On',
            headerAlign: 'center',
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.65rem' }}>
                    {params.row.createdAt ? formatDateTime(params.row.createdAt) : ''}
                </Typography>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            flex: 0.8,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ p: 1, display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <IconButton
                        sx={{ color: 'blue' }}
                        onClick={() => navigate(`/customer-app/home-screen/section/view/${params.row.id}`)}
                        size="small"
                    >
                        <RemoveRedEye sx={{ fontSize: '0.95rem' }} />
                    </IconButton>
                    <PermissionGuard feature='customer-app' permission='canEdit'>
                        <IconButton
                            onClick={() => navigate(`/customer-app/home-screen/section/${params.row.id}`)}
                            size="small"
                            color='warning'
                        >
                            <Edit sx={{ fontSize: '0.95rem' }} />
                        </IconButton>
                    </PermissionGuard>
                    <PermissionGuard feature='customer-app' permission='canDelete'>
                        <IconButton
                            onClick={() => handleDeleteSection(params.row.id)}
                            size="small"
                            color='error'
                        >
                            <Delete sx={{ fontSize: '0.95rem' }} />
                        </IconButton>
                    </PermissionGuard>
                </Box>
            )
        }
    ];

    return (
        <Box sx={{ overflow: 'auto' }}>
            <Box sx={{ mb: 2, maxWidth: 200 }}>
                <TextField
                    select
                    label="Filter by Type"
                    value={paginationModel.pageType || 'header'}
                    onChange={(e) => handleFilterChange({ pageType: e.target.value })}
                    fullWidth
                    size="small"
                >
                    <MenuItem value="header">Header</MenuItem>
                    <MenuItem value="footer">Footer</MenuItem>
                    <MenuItem value="section">Section</MenuItem>
                </TextField>
            </Box>

            <Grid container spacing={2}>
                {/* DataGrid */}
                <Grid item xs={12} lg={9}>
                    <Box sx={{ width: '100%', display: 'table', tableLayout: 'fixed', backgroundColor: '#fff', borderRadius: '.5rem' }}>
                        <DataGrid
                            rows={list}
                            columns={columns.map((col) => ({
                                ...col,
                                minWidth: isSm && (col.minWidth || 150)
                            }))}
                            getRowId={(row) => row.id}
                            rowCount={allRowCount}
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            paginationModel={paginationModel}
                            paginationMode="server"
                            onPaginationModelChange={handlePaginationChange}
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={handleSortingChange}
                            disableMultiSort
                            loading={loading}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            slots={{ loadingOverlay: Loading, noRowsOverlay: NoRowsLayout }}
                            autoHeight
                            getRowHeight={() => 'auto'}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    border: 1, borderRight: 0, borderTop: 0,
                                    borderColor: 'lightgrey',
                                    display: 'flex', alignItems: 'center',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                    borderTop: '2px solid rgba(224, 224, 224, 1)',
                                    fontWeight: 600, fontSize: '0.75rem',
                                    p: 0, height: 'fit-content',
                                    minHeight: '0 !important', height: '2rem'
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderLeft: '1px solid rgba(224, 224, 224, 1)',
                                    '&:last-child': { borderRight: 'none' },
                                },
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 600, color: '#3e3e3e'
                                },
                                '& .MuiDataGrid-row': {
                                    '&:last-child .MuiDataGrid-cell': {
                                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                    },
                                    '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                                },
                                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                            }}
                        />
                    </Box>
                </Grid>

                {/* Reorder Panel */}
                <Grid item xs={12} lg={3}>
                    <Box sx={{ backgroundColor: '#fff', borderRadius: '.5rem', p: 1.5, position: 'sticky', top: 16 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5, color: '#3e3e3e' }}>
                            Reorder Sections
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#666', mb: 1 }}>
                            Drag active items to reorder
                        </Typography>
                        <Box sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
                            {reorderList.length > 0 ? reorderList.map((item, index) => (
                                <Box
                                    key={item.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        mb: 1,
                                        opacity: draggedIndex === index ? 0.4 : 1,
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' },
                                        position: 'relative',
                                        ...(dragOverIndex === index && draggedIndex !== index && {
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 0,
                                                border: '2px dashed #2196f3',
                                                borderRadius: '4px',
                                                zIndex: 1,
                                                pointerEvents: 'none'
                                            }
                                        })
                                    }}
                                >
                                    <Card sx={{
                                        '&:hover': { boxShadow: 3, transform: 'translateY(-1px)', transition: 'all 0.2s ease' },
                                        backgroundColor: item.backgroundColor || '#fff',
                                    }}>
                                        <CardContent sx={{ p: '8px !important' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    backgroundColor: 'primary.main', color: 'white',
                                                    borderRadius: '50%', minWidth: 26, height: 26,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.7rem', fontWeight: 'bold', flexShrink: 0
                                                }}>
                                                    {index + 1}
                                                </Box>
                                                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} noWrap>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                                                        {item.sectionType?.replace(/_/g, ' ')}
                                                    </Typography>
                                                </Box>
                                                <DragIndicator sx={{ color: '#bbb', fontSize: '18px', flexShrink: 0 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )) : (
                                <Typography sx={{ fontSize: '0.7rem', color: '#999', textAlign: 'center', py: 2 }}>
                                    No active items to reorder
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default HeaderManagement;